import type { TranscriptBannerKey } from "@performyard/models";
import type { MeetingModelJson } from "@performyard/service-contracts";

import { VIDEO_CONFERENCING_PROVIDER_CONFIG } from "../../../../components/VideoConferencing/config";

/**
 * The transcript banner/chip state for a meeting. "scheduled" (meeting not yet ended, transcript
 * expected) shows the awaiting banner and dismisses via the "awaiting" key; the other non-"none"
 * values are dismissable banner keys.
 */
export type TranscriptBannerStateKind = "none" | "scheduled" | TranscriptBannerKey;

type DeriveTranscriptBannerStateInput = {
	isCreator: boolean;
	videoConference: MeetingModelJson["videoConference"];
	companyVcEnabled: boolean;
	connectedProviders: string[];
	/** Whether the meeting's scheduled end has passed; consulted only through per-provider policy. */
	meetingHasEnded: boolean;
	scheduledEnd: Date;
	scheduledDurationMinutes: number;
	createdAt: Date;
	/** Passed in rather than read from the clock so the resolver stays pure and testable. */
	now: Date;
};

const MINUTE_MS = 60 * 1000;
const DELAYED_CUTOFF_MINUTES = 24 * 60;

type TranscriptOverdueState = "within-window" | "delayed" | "expired";

const resolveTranscriptOverdueState = (
	transcriptDelay: { durationMultiple: number; minimumMinutes: number },
	{
		scheduledEnd,
		scheduledDurationMinutes,
		createdAt,
		now,
	}: Pick<DeriveTranscriptBannerStateInput, "scheduledEnd" | "scheduledDurationMinutes" | "createdAt" | "now">
): TranscriptOverdueState => {
	// A back-dated meeting is created after its own scheduled end, so the end alone would read as overdue
	// the moment it's saved.
	const waitStartedAt = Math.max(scheduledEnd.getTime(), createdAt.getTime());
	// Math.max(NaN, floor) is NaN, which would disable both states rather than fall back to the floor.
	const durationMinutes = Number.isFinite(scheduledDurationMinutes) ? Math.max(scheduledDurationMinutes, 0) : 0;
	const windowMinutes = Math.max(transcriptDelay.durationMultiple * durationMinutes, transcriptDelay.minimumMinutes);
	const elapsedMinutes = (now.getTime() - waitStartedAt) / MINUTE_MS;

	if (!Number.isFinite(elapsedMinutes) || elapsedMinutes < windowMinutes) return "within-window";
	return elapsedMinutes >= windowMinutes + DELAYED_CUTOFF_MINUTES ? "expired" : "delayed";
};

/**
 * Pure precedence resolver for the transcript banner state. Order matters: a disconnected provider
 * masks an "awaiting" that can never resolve (a disconnect stops the provider's transcript webhook).
 */
export const deriveTranscriptBannerState = ({
	isCreator,
	videoConference: vc,
	companyVcEnabled,
	connectedProviders,
	meetingHasEnded,
	scheduledEnd,
	scheduledDurationMinutes,
	createdAt,
	now,
}: DeriveTranscriptBannerStateInput): TranscriptBannerStateKind => {
	// Every state here is the meeting creator's concern; participants only ever see the transcript.
	if (!isCreator) return "none";

	const ingestStatus = vc?.transcriptIngest?.status;
	const hasTranscript = ingestStatus === "succeeded" || ingestStatus === "too_long";

	// The company turned video conferencing off but this meeting still has a VC link. No transcript will
	// arrive (ingest is blocked while disabled), so explain that instead of the awaiting/pmi/disconnected
	// states — all of which imply a transcript is still coming. A terminal outcome (succeeded/failed/
	// too_long) predates the disable, so let those fall through to their normal banners.
	if (vc && !companyVcEnabled && (ingestStatus === undefined || ingestStatus === "awaiting")) {
		return "video_conferencing_disabled";
	}
	// Meeting had a VC link, the creator's provider is no longer connected, and no transcript arrived:
	// the webhook will never come, so surface the disconnect rather than a perpetual "awaiting".
	if (vc && !connectedProviders.includes(vc.provider) && !hasTranscript) return "disconnected";
	// The provider says this meeting can never auto-retrieve (e.g. a Zoom PMI meeting).
	const providerConfig = vc ? VIDEO_CONFERENCING_PROVIDER_CONFIG[vc.provider] : undefined;
	const blockedBannerKey = vc && providerConfig?.getTranscriptBlockedBannerKey?.(vc);
	if (blockedBannerKey) return blockedBannerKey;
	// A processed transcript's outcome is real — always surface it. The settings snapshot below is a
	// create-time guess; the user may have enabled audio transcription after create, so once a
	// transcript actually arrives we honor too_long/failed regardless of that snapshot.
	if (ingestStatus === "failed") return "failed";
	if (ingestStatus === "too_long") return "too_long";
	// "awaiting"/"scheduled" are only a promise. Suppress them when the meeting's recorded settings
	// won't produce a transcript on their own (per-provider judgment from the create-time snapshot),
	// so we don't imply a transcript is coming that never will.
	const transcriptExpected = !!vc && !!providerConfig?.expectsAutomaticTranscript(vc);
	// Nothing ever marks a missed notification `failed`, so without this the promise stands forever.
	if (transcriptExpected && providerConfig && (ingestStatus === undefined || ingestStatus === "awaiting")) {
		const overdueState = resolveTranscriptOverdueState(providerConfig.transcriptDelay, {
			scheduledEnd,
			scheduledDurationMinutes,
			createdAt,
			now,
		});
		if (overdueState === "delayed") return "delayed";
		// Bounded on purpose: past the cutoff calling it merely delayed is no longer honest, and
		// `failed` still carries the retrieval trigger.
		if (overdueState === "expired") return "failed";
	}
	if (ingestStatus === "awaiting") return transcriptExpected ? "awaiting" : "none";
	// No ingest activity yet but set up for automatic retrieval: promise the transcript — same banner
	// as awaiting, without the chip (PY-13391). Providers without a meeting-ended webhook promote to
	// "awaiting" once the scheduled end passes (the transcript is now genuinely expected any moment).
	if (ingestStatus === undefined && transcriptExpected) {
		return meetingHasEnded && providerConfig?.promotesScheduledToAwaitingAfterEnd ? "awaiting" : "scheduled";
	}
	// Company has video conferencing on, this meeting has no VC link, and the creator hasn't connected.
	if (companyVcEnabled && !vc && connectedProviders.length === 0) return "discoverability";
	return "none";
};
